import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { cancelDriverBooking, getDriverBooking, sendDriverMessage } from "../../lib/driverApi";
import type { DriverBooking } from "../../lib/driverApi";
import { useDriver } from "../../lib/driverContext";
import { errorText } from "../../lib/api";
import { toast } from "../../lib/toast";
import {
  ActionButton,
  BackLink,
  Card,
  CardTitle,
  DetailRow,
  MessageBubble,
  MessageInput,
  StatusBadge,
} from "../../components/company/ui";
import { eur, longDate, shortDateTime } from "../../lib/format";
import { dailyRate } from "../../lib/rentalPrice";
import { useT } from "../../i18n";

/** One booking from the driver's side: details, company contact once accepted, and the conversation. */
export default function DriverBookingDetail() {
  const t = useT();
  const p = t.driverPages.booking;
  const { id = "" } = useParams();
  const { refreshSummary } = useDriver();
  const [booking, setBooking] = useState<DriverBooking | null>(null);
  const [error, setError] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  // Opening the booking marks the company's messages as read
  const load = useCallback(
    () =>
      getDriverBooking(id)
        .then((b) => {
          setBooking(b);
          refreshSummary();
        })
        .catch((err) => setError(errorText(err))),
    [id, refreshSummary],
  );

  useEffect(() => {
    void load();
    // New replies while the page is open
    const timer = setInterval(() => void load(), 20_000);
    return () => clearInterval(timer);
  }, [load]);

  if (error && !booking) return <p className="text-sm text-muted">{p.notFound}</p>;
  if (!booking) return <p className="text-sm text-muted">{t.common.loading}</p>;

  const b = booking;
  const name = b.listing ? `${b.listing.brand} ${b.listing.model}` : b.ref;
  const canCancel = ["pending", "documents_required", "confirmed"].includes(b.status);
  const closed = ["declined", "cancelled", "expired"].includes(b.status);
  const meta = (from: "company" | "driver" | "system", at: string) =>
    `${from === "driver" ? p.you : from === "company" ? (b.company?.name ?? "") : p.platform} · ${shortDateTime(at)}`;

  const send = async () => {
    const message = text.trim();
    if (!message) return;
    setText("");
    try {
      setBooking(await sendDriverMessage(b.ref, message));
    } catch (err) {
      toast.error(err);
      setText(message);
    }
  };

  const cancel = async () => {
    if (!window.confirm(p.cancelConfirm)) return;
    setBusy(true);
    try {
      setBooking(await cancelDriverBooking(b.ref));
      toast.success(p.cancelled);
      refreshSummary();
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <BackLink to="/driver/bookings">{p.back}</BackLink>
      <div className="mt-2.5 mb-4.5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="m-0 text-[26px] font-medium">
            {p.title} <span className="font-mono">{b.ref}</span>
          </h1>
          <div className="mt-1 text-sm text-muted">{p.sent(shortDateTime(b.createdAt))}</div>
        </div>
        <StatusBadge kind="booking" status={b.status} className="px-2.5 py-1.5 text-[13px]" />
      </div>

      {b.status === "declined" && (
        <div className="mb-4 rounded-card bg-tone-red-bg px-4 py-3 text-sm text-[#7A1A10]">{p.declined(b.declineReason)}</div>
      )}

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="grid min-w-0 gap-4">
          <Card>
            <DetailRow label={p.vehicle} value={`${name}${b.listing?.city ? ` · ${b.listing.city}` : ""}`} />
            {b.listing?.pickupAddress ? (
              <DetailRow label={p.pickup} value={b.listing.pickupAddress} />
            ) : (
              !closed && <DetailRow label={p.pickup} value={<span className="text-muted">{p.pickupHidden}</span>} />
            )}
            <DetailRow label={p.company} value={b.company?.name ?? "—"} />
            <DetailRow label={p.period} value={t.driverPages.bookings.period(longDate(b.start), longDate(b.end), b.days)} />
            <DetailRow label={p.rate} value={`${eur(dailyRate(b.terms))}${t.common.perDay} · ${eur(b.terms.weekly)}${t.common.perWeekShort} · ${eur(b.terms.monthly)}${t.common.perMonth}`} />
            {b.terms.estimate != null && <DetailRow label={p.estimate} value={eur(b.terms.estimate)} />}
            <DetailRow label={p.deposit} value={p.depositValue(eur(b.terms.deposit))} />
            {b.vehicle && <DetailRow label={p.unit} value={`${b.vehicle.code}${b.vehicle.plate ? ` · ${b.vehicle.plate}` : ""}`} />}
          </Card>

          <Card>
            <CardTitle className="mb-2.5">{p.messagesTitle}</CardTitle>
            <div className="grid gap-2">
              {(b.messages ?? []).map((m) => (
                <MessageBubble key={m.id} me="driver" from={m.from} text={m.text} meta={meta(m.from, m.at)} />
              ))}
            </div>
            {!b.messages?.length && <p className="m-0 text-sm text-faint">{p.noMessages}</p>}
            {!closed && (
              <div className="mt-3">
                <MessageInput value={text} onChange={setText} placeholder={p.placeholder} onSend={() => void send()} />
              </div>
            )}
          </Card>
        </div>

        <div className="grid gap-4">
          <Card>
            <CardTitle className="mb-2">{p.contactTitle}</CardTitle>
            {b.company?.phone || b.company?.email ? (
              <div className="grid gap-1 text-[13px]">
                <b className="font-medium">{b.company.name}</b>
                {b.company.phone && <a href={`tel:${b.company.phone.replace(/\s/g, "")}`}>{b.company.phone}</a>}
                {b.company.email && <a href={`mailto:${b.company.email}`}>{b.company.email}</a>}
                {b.company.address && <span className="text-muted">{b.company.address}</span>}
              </div>
            ) : (
              <p className="m-0 text-[13px] text-muted">{p.contactHidden}</p>
            )}
            {b.company?.publicId && (
              <Link to={`/companies/${b.company.publicId}`} className="mt-2 inline-block text-[13px] text-brand-link">
                {t.common.companyLabel(b.company.publicId)}
              </Link>
            )}
          </Card>

          {(canCancel || b.status === "completed") && (
            <Card className="grid gap-2">
              {b.status === "completed" && (
                <Link
                  to="/driver/reviews"
                  className="flex h-10 items-center justify-center rounded bg-brand text-sm font-medium text-white no-underline hover:bg-brand-dark"
                >
                  {p.review}
                </Link>
              )}
              {canCancel && (
                <ActionButton variant="outlineRed" size="lg" disabled={busy} onClick={() => void cancel()}>
                  {p.cancel}
                </ActionButton>
              )}
            </Card>
          )}

          {!!b.history?.length && (
            <Card>
              <CardTitle className="mb-2">{p.history}</CardTitle>
              <ul className="m-0 grid list-none gap-1.5 p-0 text-[13px]">
                {b.history.map((h, i) => (
                  <li key={i} className="flex justify-between gap-2">
                    <span>{t.company.status.booking[h.status]}</span>
                    <span className="text-xs text-muted">{shortDateTime(h.at)}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}
